import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { submitUsdcPayout } from '../api'
import { isLikelyEvm } from '../_shared/format'
import { loadToken, type DecodedToken } from '../_shared/token'
import {
  Badge,
  Button,
  Card,
  ErrorPanel,
  Field,
  Footer,
  FullPageLoader,
  Header,
  Page,
  TextInput,
} from '../_shared/ui'

export default function UsdcForm() {
  const { surveyId = '' } = useParams()
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const [token, setToken] = useState<string | null>(null)
  const [decoded, setDecoded] = useState<DecodedToken | null>(null)
  const [bootstrapped, setBootstrapped] = useState(false)

  const [wallet, setWallet] = useState('')
  const [consent, setConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<{ wallet?: string; consent?: string }>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    const { token: t, decoded: d } = loadToken(surveyId, search.toString())
    setToken(t)
    setDecoded(d)
    setBootstrapped(true)
  }, [surveyId, search])

  if (!bootstrapped) return <FullPageLoader />
  if (!token || !decoded || decoded.expired) {
    return (
      <Page>
        <Header />
        <ErrorPanel title="Session expired" body="Please reopen the payment link your sponsor sent you." />
        <Footer surveyId={surveyId} />
      </Page>
    )
  }

  const walletValid = !wallet || isLikelyEvm(wallet)

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const errs: typeof fieldErrors = {}
    if (!wallet.trim()) errs.wallet = 'Required'
    else if (!isLikelyEvm(wallet)) errs.wallet = 'Must be a 0x-prefixed 40-character address'
    if (!consent) errs.consent = 'You must consent to submit'
    setFieldErrors(errs)
    if (Object.keys(errs).length > 0) return

    setSubmitting(true)
    setSubmitError(null)
    submitUsdcPayout({
      surveyId,
      panellistToken: token!,
      tier: decoded!.tier,
      wallet,
      network: 'polygon',
      consent,
    }).then((res) => {
      setSubmitting(false)
      if (!res.ok) {
        setSubmitError(humanError(res.error))
        return
      }
      navigate(`/p/${surveyId}/confirm`, { state: res.data })
    })
  }

  return (
    <Page>
      <Header
        surveyName="USDC payout"
        sponsorName={`Tier: ${decoded.tier}`}
        badge={<Badge variant="info">Polygon · ~30s</Badge>}
      />

      <form onSubmit={onSubmit}>
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--cp-sp-4)' }}>
            <Field
              label="Polygon wallet address"
              hint="0x... (40 hex chars). Make sure this wallet supports Polygon USDC."
              error={fieldErrors.wallet}
            >
              <TextInput
                value={wallet}
                onChange={setWallet}
                placeholder="0x..."
                autoComplete="off"
                spellCheck={false}
                invalid={!walletValid}
                maxLength={42}
                inputMode="text"
              />
            </Field>

            <div
              style={{
                background: 'var(--cp-warning-bg)',
                border: '1px solid var(--cp-warning)',
                borderRadius: 'var(--cp-radius-md)',
                padding: '10px 12px',
                fontSize: 'var(--cp-text-sm)',
                color: 'var(--cp-warning-dark)',
              }}
            >
              <strong>Triple-check the wallet address.</strong> Crypto payments are irreversible. We
              cannot recover funds sent to the wrong address.
            </div>

            <label
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                fontSize: 'var(--cp-text-sm)',
                color: 'var(--cp-fg-secondary)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>
                I confirm the wallet address above is mine and supports Polygon USDC, and I
                consent to AsterPay processing my data for this payment only.
              </span>
            </label>
            {fieldErrors.consent && (
              <span style={{ fontSize: 'var(--cp-text-sm)', color: 'var(--cp-danger)' }}>
                {fieldErrors.consent}
              </span>
            )}

            {submitError && (
              <div
                style={{
                  background: 'var(--cp-danger-bg)',
                  color: 'var(--cp-danger-dark)',
                  borderRadius: 'var(--cp-radius-md)',
                  padding: '10px 12px',
                  fontSize: 'var(--cp-text-sm)',
                }}
              >
                {submitError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" onClick={() => navigate(`/p/${surveyId}`)} disabled={submitting}>
                Back
              </Button>
              <Button type="submit" loading={submitting} fullWidth>
                Submit wallet
              </Button>
            </div>
          </div>
        </Card>
      </form>

      <Footer surveyId={surveyId} />
    </Page>
  )
}

function humanError(err: string): string {
  switch (err) {
    case 'invalid_wallet': return 'Please enter a valid 0x-prefixed 40-character wallet address.'
    case 'invalid_token': return 'Your payment link is no longer valid.'
    case 'token_survey_mismatch':
    case 'token_tier_mismatch': return 'The payment link does not match this survey or tier.'
    case 'survey_not_accepting_payouts': return 'This survey is no longer accepting payouts.'
    case 'method_not_enabled': return 'USDC payouts are not enabled for this survey.'
    case 'already_submitted': return 'You have already submitted this payout.'
    case 'survey_not_configured': return 'This survey is not fully configured. Please contact the sponsor.'
    case 'unsupported_network': return 'Only Polygon USDC is supported right now.'
    default: return `Could not submit (${err}).`
  }
}
